<template>
  <div class="blog-post">
    <div v-if="loading" class="flex items-center justify-center h-screen">
      <div class="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <div v-else-if="error" class="flex items-center justify-center h-screen">
      <div class="text-red-500 text-xl">{{ error }}</div>
    </div>

    <template v-else-if="post">
      <!-- Header section with background image -->
      <div class="relative h-48 sm:h-64 md:h-80 mb-6 sm:mb-8">
        <img v-if="post.image" :src="post.image" :alt="post.title" class="w-full h-full object-cover object-center" />
        <div v-else class="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600"></div>
        <div class="absolute inset-0 bg-black opacity-50"></div>
        <div class="absolute inset-0 flex flex-col items-center justify-center p-4">
            <h1 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center">{{ post.title }}</h1>
            <!-- タグの表示 -->
            <div v-if="post.tags && post.tags.length" class="flex justify-center mt-2 sm:mt-4 flex-wrap gap-2">
                <span v-for="tag in post.tags" :key="tag"
                    class="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-200 bg-opacity-50 text-white">
                    #{{ tag }}
                </span>
            </div>
        </div>
      </div>

      <div class="blog-content-wrapper">
        <div class="blog-content">
          <!-- Author and date info -->
          <div class="flex items-center text-gray-600 dark:text-gray-400 mb-8">
            <img :src="post.author?.avatar || 'https://placehold.co/40'" alt="Author avatar" class="w-10 h-10 rounded-full mr-4">
            <div>
              <p class="font-semibold">{{ post.author?.username || 'Unknown Author' }}</p>
              <p class="text-sm flex items-center space-x-2">
                <span>{{ formatDate(post.createdAt) }} · {{ post.readingTime || '5 min read' }}</span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {{ post.viewCount || 0 }}
                </span>
              </p>
            </div>
          </div>

          <!-- Post content -->
          <div class="prose dark:prose-invert lg:prose-xl max-w-none mb-12 prose-img:rounded-xl prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-pre:overflow-x-auto" v-html="renderedContent"></div>

          <!-- Social sharing, like and bookmark buttons -->
          <div class="flex flex-wrap gap-3 mb-8">
            <button @click="shareOnTwitter" class="px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 transition-colors duration-200">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              <span class="whitespace-nowrap">Share on Twitter</span>
            </button>
            <button @click="shareOnFacebook" class="px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 transition-colors duration-200">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span class="whitespace-nowrap">Share on Facebook</span>
            </button>
            
            <!-- Like button -->
            <button 
              v-if="isAuthenticated"
              @click="toggleLike" 
              class="px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base"
              :class="post.isLiked 
                ? 'border border-red-500 text-red-500 bg-red-50 dark:bg-red-900/50 transition-colors duration-200' 
                : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white transition-colors duration-200'"
            >
              <!-- Use different SVG icons for liked vs. not liked state -->
              <svg v-if="post.isLiked" class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              <svg v-else class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              <span class="whitespace-nowrap">{{ post.isLiked ? 'Liked' : 'Like' }} ({{ post.likes || 0 }})</span>
            </button>
            
            <!-- Bookmark button -->
            <button 
              v-if="isAuthenticated"
              @click="toggleBookmark" 
              class="px-3 sm:px-4 py-2 rounded-md flex items-center text-sm sm:text-base"
              :class="post.isBookmarked 
                ? 'border border-blue-500 text-blue-500 bg-blue-50 dark:bg-blue-900/50 transition-colors duration-200' 
                : 'border border-gray-300 text-gray-700 hover:bg-gray-500 hover:text-white dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white transition-colors duration-200'"
            >
              <!-- Use different SVG icons for bookmarked vs. not bookmarked state -->
              <svg v-if="post.isBookmarked" class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
              <svg v-else class="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
              </svg>
              <span class="whitespace-nowrap">{{ post.isBookmarked ? 'Bookmarked' : 'Bookmark' }}</span>
            </button>
          </div>

          <!-- Edit and Delete buttons for authenticated users -->
          <div v-if="isAuthenticated && (isAuthor || isAdmin)" class="flex justify-end space-x-2 mb-8">
            <button
              @click="startEdit"
              class="px-4 py-2 rounded-md border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Edit
            </button>
            <button
              @click="deletePost"
              class="px-4 py-2 rounded-md border border-red-500 text-red-500 hover:bg-red-500 hover:text-white dark:border-red-400 dark:text-red-400 dark:hover:bg-red-400 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Delete
            </button>
          </div>

          <!-- Comments section -->
          <div class="comments bg-gray-100 dark:bg-gray-800 rounded-lg p-6">
            <h2 class="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Comments</h2>
            <div v-if="post.comments && post.comments.length">
              <div v-for="comment in post.comments" :key="comment.id" class="mb-4 p-4 bg-white dark:bg-gray-700 rounded-lg shadow">
                <div class="flex items-center mb-2">
                  <img :src="comment.authorAvatar || 'https://placehold.co/32'" alt="Commenter avatar" class="w-8 h-8 rounded-full mr-3">
                  <div>
                    <p class="font-semibold text-gray-800 dark:text-gray-200">{{ comment.author }}</p>
                    <p class="text-sm text-gray-600 dark:text-gray-400">{{ formatDate(comment.createdAt) }}</p>
                  </div>
                </div>
                <p class="text-gray-700 dark:text-gray-300">{{ comment.content }}</p>
              </div>
            </div>
            <div v-else class="text-gray-600 dark:text-gray-400">No comments yet.</div>
            <form v-if="isAuthenticated" @submit.prevent="addComment" class="mt-6">
              <textarea 
                v-model="newComment" 
                rows="4" 
                class="w-full px-3 py-2 text-gray-700 dark:text-gray-300 border rounded-lg focus:outline-none focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600" 
                placeholder="Add a comment..."
              ></textarea>
              <button 
                type="submit" 
                class="mt-2 font-bold py-2 px-4 rounded-lg border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400 dark:hover:text-gray-900 transition-colors duration-200"
              >
                Post Comment
              </button>
            </form>
          </div>
        </div>

        <!-- Related Posts section -->
        <div v-if="relatedPosts.length" class="mt-8 sm:mt-12">
          <h2 class="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Related Posts</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div v-for="relatedPost in relatedPosts" :key="relatedPost.id" class="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              <img :src="relatedPost.image || 'https://placehold.co/300x200'" alt="Related post thumbnail" class="w-full h-36 sm:h-48 object-cover">
              <div class="p-3 sm:p-4">
                <h3 class="text-base sm:text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200 line-clamp-2">{{ relatedPost.title }}</h3>
                <p class="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{{ relatedPost.excerpt }}</p>
                <router-link :to="{ name: 'BlogPost', params: { id: relatedPost.id } }" class="text-blue-500 hover:underline text-sm">Read more</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="flex items-center justify-center h-screen">
      <div class="text-gray-500 text-xl">No post found.</div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { formatDate } from '@/utils/date'
import { marked } from 'marked'

export default {
  name: 'BlogPost',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const newComment = ref('')

    const post = computed(() => store.state.currentPost)
    const relatedPosts = computed(() => store.state.relatedPosts)
    const loading = computed(() => store.state.loading)
    const error = computed(() => store.state.error)
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const currentUser = computed(() => store.state.user)
    const isAuthor = computed(() =>
      currentUser.value &&
      post.value &&
      post.value.author &&
      currentUser.value.id === post.value.author.id
    )
    const isAdmin = computed(() => currentUser.value && currentUser.value.isAdmin)
    const renderedContent = computed(() => {
      if (!post.value || !post.value.content) return ''
      return marked(post.value.content, {
        breaks: true,
        gfm: true,
        sanitize: true
      })
    })

    // Update meta tags when post data is loaded
    watch(post, (newPost) => {
      if (newPost) {
        updateMetaTags(newPost)
        addJsonLdScript(newPost)
      }
    }, { deep: true })

    // Function to update meta tags dynamically
    const updateMetaTags = (post) => {
      // Update title
      document.title = `${post.title} - Skix Inc Tech Blog`
      
      // Remove existing meta tags
      Array.from(document.querySelectorAll('[data-dynamic-meta]')).forEach(el => el.remove())
      
      // Create and add new meta tags
      const metaTags = [
        { name: 'description', content: post.excerpt || post.title },
        { property: 'og:title', content: post.title },
        { property: 'og:description', content: post.excerpt || post.title },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: window.location.href },
        { property: 'og:image', content: post.image || 'https://blog.skix.io/images/og-image.jpg' },
        { property: 'article:published_time', content: post.createdAt },
        { property: 'article:author', content: post.author?.username || 'Skix Inc' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: post.title },
        { name: 'twitter:description', content: post.excerpt || post.title },
        { name: 'twitter:image', content: post.image || 'https://blog.skix.io/images/og-image.jpg' }
      ]
      
      // Add tags for each tag on the post
      if (post.tags && post.tags.length) {
        post.tags.forEach(tag => {
          metaTags.push({ property: 'article:tag', content: tag })
        })
      }
      
      // Add meta tags to document head
      metaTags.forEach(tagDef => {
        const tag = document.createElement('meta')
        Object.keys(tagDef).forEach(key => {
          tag.setAttribute(key, tagDef[key])
        })
        tag.setAttribute('data-dynamic-meta', '')
        document.head.appendChild(tag)
      })
      
      // Update canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]')
      if (!canonicalLink) {
        canonicalLink = document.createElement('link')
        canonicalLink.setAttribute('rel', 'canonical')
        canonicalLink.setAttribute('data-dynamic-meta', '')
        document.head.appendChild(canonicalLink)
      }
      canonicalLink.setAttribute('href', window.location.href)
    }
    
    // Function to add JSON-LD structured data
    const addJsonLdScript = (post) => {
      // Remove existing JSON-LD script if any
      const existingScript = document.querySelector('script[type="application/ld+json"][data-post-jsonld]')
      if (existingScript) {
        existingScript.remove()
      }
      
      // Create structured data for the blog post
      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        'headline': post.title,
        'description': post.excerpt || '',
        'image': post.image || 'https://tech-blog.skixinc.com/images/og-image.jpg',
        'datePublished': post.createdAt,
        'dateModified': post.updatedAt || post.createdAt,
        'author': {
          '@type': 'Person',
          'name': post.author?.username || 'Skix Inc'
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Skix Inc',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://blog.skix.io/images/logo.png'
          }
        },
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': window.location.href
        }
      }
      
      // Add tags as keywords if available
      if (post.tags && post.tags.length) {
        jsonLd.keywords = post.tags.join(',')
      }
      
      // Create and add the script element
      const script = document.createElement('script')
      script.setAttribute('type', 'application/ld+json')
      script.setAttribute('data-post-jsonld', '')
      script.textContent = JSON.stringify(jsonLd)
      document.head.appendChild(script)
    }

    onMounted(async () => {
      const postId = route.params.id;
      await store.dispatch('fetchPost', postId);
      await store.dispatch('fetchRelatedPosts', postId);

      // Increment view count after 10 seconds
      setTimeout(() => {
        store.dispatch('incrementViewCount', postId);
      }, 10000); // 10 seconds delay
    });

    const deletePost = async () => {
      if (confirm('Are you sure you want to delete this post?')) {
        try {
          await store.dispatch('deletePost', post.value.id)
          router.push('/')
        } catch (error) {
          console.error('Error deleting post:', error)
          alert('Failed to delete the post. Please try again.')
        }
      }
    }

    const addComment = async () => {
      if (newComment.value.trim()) {
        try {
          await store.dispatch('addComment', {
            postId: post.value.id,
            content: newComment.value
          })
          newComment.value = ''
        } catch (error) {
          console.error('Error adding comment:', error)
          alert('Failed to add the comment. Please try again.')
        }
      }
    }

    const shareOnTwitter = () => {
      const text = encodeURIComponent(`Check out this post: ${post.value.title}`)
      const url = encodeURIComponent(window.location.href)
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank')
    }

    const shareOnFacebook = () => {
      const url = encodeURIComponent(window.location.href)
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
    }

    const startEdit = () => {
      router.push(`/edit/${post.value.id}`)
    }

    // Like and Bookmark functionality
    const toggleLike = async () => {
      if (!isAuthenticated.value) return
      
      try {
        await store.dispatch('toggleLike', post.value.id)
      } catch (error) {
        console.error('Error toggling like:', error)
        alert('Failed to like/unlike the post. Please try again.')
      }
    }
    
    const toggleBookmark = async () => {
      if (!isAuthenticated.value) return
      
      try {
        await store.dispatch('toggleBookmark', post.value.id)
      } catch (error) {
        console.error('Error toggling bookmark:', error)
        alert('Failed to bookmark/unbookmark the post. Please try again.')
      }
    }

    return {
      post,
      loading,
      error,
      newComment,
      isAuthenticated,
      isAuthor,
      isAdmin,
      relatedPosts,
      formatDate,
      deletePost,
      addComment,
      shareOnTwitter,
      shareOnFacebook,
      startEdit,
      renderedContent,
      toggleLike,
      toggleBookmark
    }
  }
}
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.prose img {
  @apply rounded-lg shadow-md;
}

/* Table styles for markdown tables */
.prose table {
  @apply w-full border-collapse my-6;
}

.prose table th {
  @apply bg-gray-100 dark:bg-gray-700 text-left p-2 border border-gray-300 dark:border-gray-600 font-semibold;
}

.prose table td {
  @apply p-2 border border-gray-300 dark:border-gray-600;
}

.prose table tr:nth-child(even) {
  @apply bg-gray-50 dark:bg-gray-800;
}

.prose table tr:hover {
  @apply bg-gray-100 dark:bg-gray-700;
}

/* Fix for code blocks in published posts */
.prose pre {
  @apply bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto border border-gray-200 dark:border-gray-700;
}

.prose pre code {
  @apply bg-transparent text-gray-800 dark:text-gray-200 p-0 block w-full overflow-x-auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Ensure inline code is properly styled */
.prose code:not(pre code) {
  @apply bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.blog-content-wrapper {
  @apply px-4 sm:px-6 lg:px-8 py-8;
  max-width: 100%;
  margin: 0 auto;
}

.blog-content {
  max-width: 768px;
  margin: 0 auto;
}

@media (min-width: 1024px) {
  .blog-content-wrapper {
    max-width: 1200px;
  }
}

@media (min-width: 1280px) {
  .blog-content-wrapper {
    max-width: 1400px;
  }
}
</style>
